import { UUID as PBUUID } from "gen/proto/uuid/models_pb"
import {
  OrderCreditsBy,
  SearchCreditsReq,
  SortOrder,
} from "gen/search/service/service_pb"
import {
  CreditAllocationStatus,
  Receivable_Transaction_Type,
} from "gen/txn/models_pb"
import { useQueryAdapter } from "shared-ts/adapters"
import { FullInterval, PartialInterval } from "shared-ts/libs/dates/interval"

import { useSearchCredits } from "features/api/search"

import {
  adaptSearchCreditsResultToCreditTableItems,
  CreditReceivable,
} from "./creditHistorySearch.adapter"

interface UseCreditsTableSearchProps {
  customerIds?: PBUUID[]
  orderBy: OrderCreditsBy
  order: SortOrder
  selectedDateRange: FullInterval | null
  selectedStatuses: CreditAllocationStatus[]
  selectedTransactionTypes: Receivable_Transaction_Type[]
}

export interface UseCreditsTableSearchRes {
  creditTableLineItems: CreditReceivable[]
  searchQuery: string
  setSearchQuery: (query: string) => void
  isLoading: boolean
  isError: boolean
  loadNext: () => void
}

export function useCreditsTableSearch({
  customerIds,
  orderBy,
  order,
  selectedDateRange,
  selectedStatuses,
  selectedTransactionTypes,
}: UseCreditsTableSearchProps): UseCreditsTableSearchRes {
  const searchRequest: Omit<
    SearchCreditsReq,
    "query" | "cursor" | "offset" | "limit"
  > = {
    customerIds: customerIds ?? [],
    orderBy,
    sortOrder: order,
    dateRangeFilter: selectedDateRange
      ? new PartialInterval({
          start: selectedDateRange.start,
          end: selectedDateRange.end,
        }).toTimestampRangePB()
      : undefined,
    allocationStatuses: selectedStatuses,
    transactionTypes: selectedTransactionTypes,
  }

  const search = useSearchCredits(searchRequest)

  const { data: creditTableLineItems } = useQueryAdapter({
    query: search,
    adapter: (data) =>
      adaptSearchCreditsResultToCreditTableItems(
        data.pages.flatMap((p) => p.results)
      ),
    defaultValue: [],
  })

  return {
    creditTableLineItems,
    searchQuery: search.query,
    setSearchQuery: search.setQuery,
    isLoading: search.isLoading,
    isError: search.isError,
    loadNext: search.loadNext,
  }
}
